import React, { useState, useContext, useEffect, useMemo } from 'react'
import Topbar from '@/components/dashboard/roadmap/Topbar'
import RoadmapHeader from '@/components/dashboard/roadmap/RoadmapHeader'
import RoadmapContent from '@/components/dashboard/roadmap/RoadmapContent'
import RoadmapContentSkeleton from '@/components/dashboard/roadmap/skeletons/RoadmapContentSkeleton'
import OpportunityGridSkeleton from '@/components/dashboard/roadmap/skeletons/OpportunityGridSkeleton'
import WaveBackground from '@/components/animations/WaveBackground'
import { RoadmapContext } from '@/context/RoadmapContext'
import { DevTool } from '@/hooks/useProductConfig'
import { useParams, useNavigate } from 'react-router-dom'
import OpportunityCard from './OpportunityCard'
import RoadmapConfigurationDialog from './RoadmapConfigurationDialog'
import { EditDialog } from '@/components/dialogs/editDialog'
import { DashboardContext } from '@/context/DashboardContext'
import { createPortal } from 'react-dom'
import { customFetch } from '@/utils/fetch'

const Roadmap = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [meetingWithUrl, setMeetingWithUrl] = useState<any>(null)
  const [isLoadingMeeting, setIsLoadingMeeting] = useState(false)
  const [shouldShowDialog, setShouldShowDialog] = useState(false)
  const { dialogState, closeDialog, allProjects, meetings, participantsCache } = useContext(DashboardContext)
  const { allOpportunities, changeProduct, selectedProduct, isLoading, isLoadingRoadmap, evidence } =
    useContext(RoadmapContext)

  const filteredOpportunities = useMemo(() => {
    console.log('Recalculating filtered opportunities...', allOpportunities.length)
    if (!params.roadmapMenu) {
      return []
    }
    if (params.roadmapMenu === 'top-opportunities') {
      return allOpportunities.filter((opportunity) => !opportunity.isArchived && opportunity.type === 'epic')
    } else if (params.roadmapMenu === 'quick-wins') {
      return allOpportunities.filter((opportunity) => !opportunity.isArchived && opportunity.type === 'quick win')
    } else if (params.roadmapMenu === 'archived-opportunities') {
      return allOpportunities.filter((opportunity) => opportunity.isArchived)
    }
    return []
  }, [params.roadmapMenu, allOpportunities])

  useEffect(() => {
    if (!params.roadmapMenu) {
      navigate('/dashboard/roadmap/top-opportunities')
    }
  }, [params.roadmapMenu, navigate])

  useEffect(() => {
    async function fetchMeetingUrl() {
      if (dialogState.isOpen && dialogState.noteId) {
        setIsLoadingMeeting(true)
        setShouldShowDialog(false)
        const meeting = meetings.find((meeting) => meeting.id === evidence[dialogState.noteId].meeting_id)
        if (meeting?.stored_name) {
          try {
            const res = await customFetch(`/getMeetUrl/${meeting.id}`, 'GET', null)
            if (res.error) throw res
            setMeetingWithUrl({ ...meeting, url: res.url })
          } catch (error) {
            console.error('Error getting file URL:', error)
            setMeetingWithUrl(meeting)
          }
        } else {
          setMeetingWithUrl(meeting)
        }
        setIsLoadingMeeting(false)
        setShouldShowDialog(true)
      }
    }
    fetchMeetingUrl()
  }, [dialogState.isOpen, dialogState.noteId, meetings, evidence])

  // Reset states when dialog closes
  useEffect(() => {
    if (!dialogState.isOpen) {
      setMeetingWithUrl(null)
      setIsLoadingMeeting(false)
      setShouldShowDialog(false)
    }
  }, [dialogState.isOpen])

  const handleClose = () => {
    setShouldShowDialog(false)
    setTimeout(() => {
      closeDialog()
    }, 200) // Delay to allow animation
  }

  return (
    <div className="min-h-screen bg-canvas dark:bg-canvas-dark flex flex-col">
      <Topbar onToggleHistory={() => {}} showRoadmapNav={true} onOpenConfigDialog={() => setConfigDialogOpen(true)} />
      <div className="w-full h-[calc(100vh-70px)]">
        <WaveBackground isLoading={isLoading}>
          <div className="h-full overflow-y-auto">
            <div className="w-full px-8">
              <RoadmapHeader selectedProductId={selectedProduct.id} onProductChange={changeProduct} />
              {params.roadmapMenu === 'top-opportunities' &&
                (isLoadingRoadmap ? (
                  <RoadmapContentSkeleton capacity={Number(selectedProduct.configs?.capacity || 3)} />
                ) : (
                  <RoadmapContent
                    allOpportunities={filteredOpportunities}
                    capacity={Number(selectedProduct.configs?.capacity || 3)}
                    devTool={selectedProduct.configs?.dev_tool as DevTool}
                  />
                ))}
              {params.roadmapMenu === 'quick-wins' &&
                (isLoadingRoadmap ? (
                  <OpportunityGridSkeleton cardCount={6} />
                ) : (
                  <div className="flex flex-col">
                    <div className="p-8 pt-0">
                      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-5">
                        {filteredOpportunities.map((opportunity, index) => (
                          <OpportunityCard
                            key={opportunity.id || opportunity.title}
                            opportunity={opportunity}
                            number={index + 1}
                            devTool={selectedProduct.configs?.dev_tool as DevTool}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              {params.roadmapMenu === 'archived-opportunities' &&
                (isLoadingRoadmap ? (
                  <OpportunityGridSkeleton cardCount={4} />
                ) : (
                  <div className="flex flex-col">
                    <div className="p-8 pt-0">
                      {filteredOpportunities.length === 0 ? (
                        <div className="text-center p-8 bg-white rounded-lg shadow">
                          <h3 className="text-lg font-medium text-gray-700">No archived opportunities</h3>
                          <p className="text-gray-500 mt-2">When you archive opportunities, they'll appear here.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-5">
                          {filteredOpportunities.map((opportunity, index) => (
                            <OpportunityCard
                              key={opportunity.id || opportunity.title}
                              opportunity={opportunity}
                              number={index + 1}
                              devTool={selectedProduct.configs?.dev_tool as DevTool}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </WaveBackground>
      </div>

      {/* <VersionHistory 
        versions={mockVersions}
        onSelectVersion={handleSelectVersion}
        onRevertVersion={handleRevertVersion}
        selectedVersionId={selectedVersionId}
        isOpen={historyOpen}
      /> */}

      <RoadmapConfigurationDialog
        open={configDialogOpen}
        onOpenChange={setConfigDialogOpen}
        productId={selectedProduct.id}
        productName={selectedProduct.name || 'Product'}
      />

      {/* Render EditDialog when all conditions are met */}
      {shouldShowDialog &&
        !isLoadingMeeting &&
        meetingWithUrl &&
        (createPortal(
          <EditDialog
            entity={evidence[dialogState.noteId]}
            project={
              evidence[dialogState.noteId]?.project_id
                ? allProjects.find((project) => project.id === evidence[dialogState.noteId].project_id)
                : null
            }
            participants={participantsCache}
            tagging={false}
            rating={false}
            readOnly={true}
            entityName={dialogState.type}
            meeting={meetingWithUrl}
            close={handleClose}
            setEntities={undefined}
            categories={undefined}
            setProject={undefined}
            changeContent={undefined}
            changeCategory={undefined}
            changeColor={undefined}
            deleteEntity={undefined}
          />,
          document.body
        ) as React.ReactPortal)}
    </div>
  )
}

export default Roadmap
